import { useState, type FC } from "react";
import { Button, Box, Group, Stack, Title } from "@mantine/core";
import { AITokensUsageCard } from "./AITokensUsageCard.js";
import { CustomStylesUsageCard } from "./CustomStylesUsageCard.js";
import { CustomTilesetsUsageCard } from "./CustomTilesetsUsageCard.js";
import { MapLoadsUsageCard } from "./MapLoadsUsageCard.js";
import { ProcessingUsageCard } from "./ProcessingUsageCard.js";
import { StorageUsageCard } from "./StorageUsageCard.js";
import { TilesRequestsUsageCard } from "./TilesRequestsUsageCard.js";
import { useTRPC } from "../../../trpc.js";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage } from "react-intl";
import { useProductIds } from "../../../hooks/subscription.js";
import { APIKeySelector } from "./APIKeySelector.js";
import { useNavigate } from "@tanstack/react-router";
import { useSessionAccountName } from "../../../hooks/user.js";
import { CACHING_OPTIONS } from "./constants/cachingOptions.js";
import {
  getAccountUserProductId,
  getCustomStyleProductId,
  getCustomTilesetProductId,
  getInputTokenProductId,
  getMapSessionProductId,
  getOutputTokenProductId,
  getProcessingProductId,
  getStorageProductId,
  getTileUsageProductId,
} from "@project/common";
import { EstimatedTotalBillCard } from "./EstimatedTotalBillCard.js";
import { endOfMonth, startOfMonth } from "date-fns";
import { wrapper } from "./UsageAndBilling.module.css";
import { TeamSeatsUsageCard } from "./TeamSeatsUsageCard.js";

export const UsageAndBilling: FC = () => {
  const trpc = useTRPC();
  const navigate = useNavigate();

  const accountName = useSessionAccountName() ?? "";
  const {
    data: apiKeys = [],
  } = useQuery(trpc.getApiKeys.queryOptions(undefined, CACHING_OPTIONS));

  const [apiKeyId, setApiKeyId] = useState<number>();
  const { isLoadingSubscriptions, tieredProductIds: productIds } = useProductIds();

  const from = startOfMonth(new Date());
  const to = endOfMonth(new Date());
  const interval = "day" as const;
  const cardProps = { from, to, interval, apiKeyId, isLoadingSubscriptions };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group>
          <Title order={3}>
            <FormattedMessage
              defaultMessage="Usage & Billing"
              id="usageAndBilling.header"
            />
          </Title>
        </Group>

        <Group gap={16}>
          <Button
            px={20}
            py={8}
            variant="light"
            color="black"
            onClick={() => {
              void navigate({
                to: "/$accountName/admin/usage",
                params: { accountName },
              });
            }}
          >
            <FormattedMessage
              defaultMessage="View all statistics"
              id="usageHeader.viewStatistics"
            />
          </Button>
          <APIKeySelector {...{ apiKeys, apiKeyId, setApiKeyId }} />
        </Group>
      </Group>

      <Box className={wrapper}>
        <AITokensUsageCard
          {...cardProps}
          inputTokenProductId={getInputTokenProductId(productIds)}
          outputTokenProductId={getOutputTokenProductId(productIds)}
        />
        <ProcessingUsageCard
          {...cardProps}
          productId={getProcessingProductId(productIds)}
        />
        <MapLoadsUsageCard
          {...cardProps}
          productId={getMapSessionProductId(productIds)}
        />
        <TilesRequestsUsageCard
          {...cardProps}
          productId={getTileUsageProductId(productIds)}
        />
        <StorageUsageCard
          to={to}
          productId={getStorageProductId(productIds)}
          isLoadingSubscriptions={isLoadingSubscriptions}
        />
        <TeamSeatsUsageCard
          to={to}
          productId={getAccountUserProductId(productIds)}
          isLoadingSubscriptions={isLoadingSubscriptions}
        />
        <CustomStylesUsageCard
          to={to}
          productId={getCustomStyleProductId(productIds)}
          isLoadingSubscriptions={isLoadingSubscriptions}
        />
        <CustomTilesetsUsageCard
          to={to}
          productId={getCustomTilesetProductId(productIds)}
          isLoadingSubscriptions={isLoadingSubscriptions}
        />
        <EstimatedTotalBillCard
          interval={interval}
          apiKeyId={apiKeyId}
          productIds={productIds}
          isLoadingSubscriptions={isLoadingSubscriptions}
        />
      </Box>
    </Stack>
  );
};
